<template>
  <div class="container mx-auto px-3 py-6 sm:px-4 sm:py-8">
    <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8">
      Your Favorites
    </h1>
    
    <!-- User not signed in -->
    <div v-if="!currentUser" class="text-center py-16">
      <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
      </svg>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Sign in to save favorites</h3>
      <p class="text-gray-500 dark:text-gray-400 mb-6">Create an account to save your favorite wallpapers</p>
      <button class="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors">
        Sign In
      </button>
    </div>

    <!-- Loading state -->
    <div v-else-if="loading" class="text-center py-16">
      <div class="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
      <p class="text-gray-500 dark:text-gray-400">Loading your favorites...</p>
    </div>

    <!-- No favorites -->
    <div v-else-if="favoriteWallpapers.length === 0" class="text-center py-16">
      <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
      </svg>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">No favorites yet</h3>
      <p class="text-gray-500 dark:text-gray-400 mb-6">Start exploring and save wallpapers you love</p>
      <router-link 
        to="/categories" 
        class="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
      >
        Browse Wallpapers
      </router-link>
    </div>

    <!-- Favorites Grid -->
    <div v-else>
      <div class="flex items-center justify-between mb-6">
        <p class="text-gray-600 dark:text-gray-300">
          {{ favoriteWallpapers.length }} favorite{{ favoriteWallpapers.length !== 1 ? 's' : '' }}
        </p>
        
        <!-- View Mode Toggle -->
        <div class="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button @click="viewMode = 'grid'" :class="[
            'px-3 py-1 text-sm rounded-md transition-colors',
            viewMode === 'grid'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          ]">
            Grid
          </button>
          <button @click="viewMode = 'preview'" :class="[
            'px-3 py-1 text-sm rounded-md transition-colors',
            viewMode === 'preview'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          ]">
            Device Preview
          </button>
        </div>
      </div>
      
      <WallpaperGrid
        :wallpapers="favoriteWallpapers"
        :loading="false"
        :has-more="false"
        :view-mode="viewMode"
        @wallpaper-click="viewWallpaper"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import WallpaperGrid from '../components/WallpaperGrid.vue';
import { wallpaperService, type Wallpaper } from '../firebase/services/wallpaperService';
import { userService } from '../firebase/services/userService';
import { useAuth } from '../composables/useAuth';

const router = useRouter();

// State
const viewMode = ref('grid'); // 'grid' or 'preview'
const favoriteWallpapers = ref<Wallpaper[]>([]);
const loading = ref(false);
const { currentUser } = useAuth();

// Methods
const loadFavoriteWallpapers = async () => {
  if (!currentUser.value) {
    favoriteWallpapers.value = [];
    return;
  }
  
  loading.value = true;
  try {
    const favorites = await userService.getUserFavorites(currentUser.value.uid);
    const wallpaperPromises = favorites.map(fav => wallpaperService.getWallpaperById(fav.wallpaperId));
    const wallpapers = await Promise.all(wallpaperPromises);
    favoriteWallpapers.value = wallpapers.filter(w => w !== null) as Wallpaper[];
  } catch (error) {
    console.error('Error loading favorite wallpapers:', error);
  } finally {
    loading.value = false;
  }
};

const viewWallpaper = (wallpaper: Wallpaper) => {
  router.push(`/wallpaper/${wallpaper.id}`);
};

// Lifecycle
onMounted(async () => {
  await loadFavoriteWallpapers();
});

watch(currentUser, async () => {
  await loadFavoriteWallpapers();
}, { immediate: true });
</script>